import { Divider } from "@mui/material";
import { type ReactNode } from "react";
import { useIntl } from "react-intl";

import { Image } from "@/components/Image/Image";
import { Select, SelectItem } from "@/components/Select";
import { type PaymentType, usePaymentContext } from "@/contexts/PaymentContext";
import { includeBnplFee, calculateMonthlyInstallment } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";

type PaymentTypeRadioProps = {
  value: PaymentType;
  checked: boolean;
  onChange: (type: PaymentType) => void;
  disabled?: boolean;
  label: ReactNode;
  description: ReactNode;
  badge?: ReactNode;
};

const PaymentTypeRadio = ({
  value,
  checked,
  onChange,
  disabled,
  label,
  description,
  badge,
}: PaymentTypeRadioProps) => {
  return (
    <label className="relative flex flex-1 cursor-pointer flex-col items-start gap-y-2 rounded-xl border border-gray-200 bg-white p-4 transition-all hover:enabled:border-gray-300 has-checked:border-2 has-checked:border-core-accent-500 has-checked:bg-green-50 has-disabled:cursor-not-allowed has-disabled:opacity-60">
      <input
        type="radio"
        name="paymentType"
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="peer hidden"
        disabled={disabled}
      />
      {badge ? (
        <span className="absolute end-4 top-0 -translate-y-1/2 rounded-full bg-surface-accent-softer px-2 py-1 text-xs font-medium text-text-primary">
          {badge}
        </span>
      ) : null}
      <div className="text-sm font-medium text-text-primary">{label}</div>
      <div className="text-xs text-text-tertiary">{description}</div>
    </label>
  );
};

type PaymentChooserProps = {
  price: number;
};

export const PaymentChooser = ({ price }: PaymentChooserProps) => {
  const intl = useIntl();
  const {
    paymentType,
    handlePaymentTypeChange,
    selectedInstallments,
    availableInstallments,
    sessionId,
    setInstallments,
  } = usePaymentContext();

  const isBnplDisabled = !sessionId;

  return (
    <>
      <Image src="/bnpl.png" className="rounded-xl" />

      <div className="my-4 w-full">
        <div className="flex w-full gap-3">
          <PaymentTypeRadio
            value="standard"
            checked={paymentType === "standard"}
            onChange={handlePaymentTypeChange}
            label={intl.formatMessage({
              id: "payment.standardPayment",
              defaultMessage: "Standard",
            })}
            description={intl.formatMessage({
              id: "payment.payUpfrontDescription",
              defaultMessage: "Pay the full amount upfront",
            })}
          />

          <PaymentTypeRadio
            value="bnpl"
            checked={paymentType === "bnpl"}
            onChange={handlePaymentTypeChange}
            label={intl.formatMessage({
              id: "payment.payLaterPayment",
              defaultMessage: "Pay later",
            })}
            description={intl.formatMessage({
              id: "payment.splitPaymentDescription",
              defaultMessage:
                "Get your item now and split the cost into monthly instalments",
            })}
            badge={
              isBnplDisabled
                ? null
                : intl.formatMessage({
                    id: "payment.flexibleBadge",
                    defaultMessage: "Flexible!",
                  })
            }
            disabled={isBnplDisabled}
          />
        </div>

        <div className="mt-4 rounded-xl bg-surface-neutral-soft p-4">
          {paymentType === "standard" && (
            <>
              <p className="mb-1 text-sm font-medium text-text-title">
                {intl.formatMessage({
                  id: "payment.price",
                  defaultMessage: "Price",
                })}
              </p>
              <div className="text-xl font-bold text-text-primary">
                {formatCurrency(price)}
              </div>
            </>
          )}
          {paymentType === "bnpl" && (
            <>
              <p className="text-start text-sm font-medium text-text-primary">
                {intl.formatMessage({
                  id: "payment.calculator",
                  defaultMessage: "Create your plan",
                })}
              </p>
              <p className="mb-5 text-start text-xs text-text-tertiary">
                {intl.formatMessage({
                  id: "payment.calculatorDescription",
                  defaultMessage: "Select how long you would like to pay",
                })}
              </p>
              <Select
                label={intl.formatMessage({
                  id: "payment.numberOfInstalments",
                  defaultMessage: "Number of instalments",
                })}
                value={selectedInstallments}
                onChange={(e) => setInstallments(Number(e.target.value))}
              >
                {availableInstallments?.map((num) => (
                  <SelectItem key={num} value={num}>
                    {intl.formatMessage(
                      {
                        id: "payment.months",
                        defaultMessage: "{count} months",
                      },
                      { count: num },
                    )}
                  </SelectItem>
                ))}
              </Select>

              <Divider sx={{ my: 2 }} />

              <div className="mb-1 text-sm font-medium text-text-title">
                {intl.formatMessage({
                  id: "payment.monthlyPayment",
                  defaultMessage: "Monthly payment",
                })}
              </div>
              <div className="text-xl font-bold text-text-primary">
                <span>
                  {formatCurrency(
                    calculateMonthlyInstallment(
                      includeBnplFee(price),
                      selectedInstallments,
                    ),
                  )}
                </span>{" "}
                <span className="text-xs font-normal text-text-tertiary">
                  {intl.formatMessage({
                    id: "payment.perMonth",
                    defaultMessage: "per month",
                  })}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
